// Kleur-normalisatie: zet ruwe kleurnamen om naar hoofdkleuren voor nette filters.
// Niet-kleuren (productcodes) → "" (uitgesloten van het kleurfilter).

// De hoofdkleuren in vaste weergavevolgorde.
export const MAIN_COLORS = [
  "Wit", "Zwart", "Grijs", "Blauw", "Groen", "Rood",
  "Roze", "Paars", "Geel", "Oranje", "Bruin", "Beige", "Multi",
] as const;

export type MainColor = (typeof MAIN_COLORS)[number] | "";

// Expliciete mapping van afgeleide/fantasienamen → hoofdkleur.
const MAP: Record<string, MainColor> = {
  // Wit-tinten
  "wit": "Wit", "off white": "Wit", "wolwit": "Wit", "ivoor": "Wit",
  // Zwart
  "zwart": "Zwart",
  // Grijs-tinten
  "grijs": "Grijs", "donker grijs": "Grijs", "donkergrijs": "Grijs", "licht grijs": "Grijs",
  "lichtgrijs": "Grijs", "staal grijs": "Grijs", "staalgrijs": "Grijs", "antraciet": "Grijs", "melee": "Grijs",
  // Blauw-tinten
  "blauw": "Blauw", "donkerblauw": "Blauw", "donker blauw": "Blauw", "licht blauw": "Blauw",
  "lichtblauw": "Blauw", "marine": "Blauw", "navy": "Blauw", "indigo": "Blauw", "aqua": "Blauw", "jupiter": "Blauw",
  // Groen
  "groen": "Groen", "forest mist": "Groen",
  // Rood
  "rood": "Rood", "red velvet": "Rood", "raspberry": "Rood",
  // Roze
  "roze": "Roze", "blush": "Roze",
  // Paars
  "paars": "Paars", "lila": "Paars", "purple orchid": "Paars",
  // Geel / Oranje
  "geel": "Geel", "oranje": "Oranje",
  // Bruin / Beige
  "bruin": "Bruin", "walnoot": "Bruin",
  "beige": "Beige", "huid": "Beige", "dusty sand": "Beige",
  // Print/dessin → Multi
  "multi": "Multi", "gestreept": "Multi", "leopard": "Multi", "faded lines": "Multi", "uni": "Multi",
};

// Bolletje-/swatchkleur per hoofdkleur — één gedeelde bron voor productkaart,
// productpagina én het kleurenfilter in de zijbalk, zodat een correctie
// (zoals "Blauw" te kobaltachtig, hoort donkerblauw te zijn) overal
// tegelijk doorwerkt i.p.v. in losse, uit elkaar lopende kopieën.
export const COLOR_SWATCH_HEX: Record<string, string> = {
  Wit: "#ffffff", Zwart: "#1a1a1a", Grijs: "#9ca3af",
  // Was #3b82f6/#2563eb (kobaltblauw) — de meeste producten in deze groep
  // ("blauw", "donkerblauw", "marine", "navy" e.d.) zijn feitelijk donkerblauw.
  Blauw: "#1e3a5f",
  Groen: "#16a34a", Rood: "#dc2626", Roze: "#ec4899", Paars: "#7c3aed",
  Geel: "#eab308", Oranje: "#f97316", Bruin: "#92400e", Beige: "#d4b896",
  Multi: "linear-gradient(135deg,#ec4899,#3b82f6,#22c55e,#eab308)",
};

// Patronen die GEEN kleur zijn (productcodes / stofnamen).
const NON_COLOR = /^(rico-?\d|cb\d|m\d{2,}|startex|m55)/i;

export function toMainColor(raw?: string): MainColor {
  if (!raw) return "";
  const v = raw.trim().toLowerCase();
  if (!v) return "";
  if (NON_COLOR.test(v)) return "";
  // Combinaties zoals "Blauw/Groen" → Multi
  if (v.includes("/")) return "Multi";
  if (MAP[v]) return MAP[v];
  // Fallback: bevat een hoofdkleur als woord?
  for (const c of MAIN_COLORS) {
    if (v.includes(c.toLowerCase())) return c;
  }
  return ""; // onbekend / geen kleur
}
